<template>
    <DrawerAccordion
        v-if="hasCompany"
        id="corp"
        :title="translations.title"
        coloredIcon="company"
        openByDefault
    >
        <template #action>
            <CompassPlainButton
                v-if="showEditButton"
                :label="translations.edit"
                :size="CompassEnums.PlainButtonSize.SMALL"
                :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
                @click.stop="handleEdit"
            />
        </template>
        <div class="overview-company-block">
            <div class="overview-company-block__company">
                <CompassTooltip
                    :content="translations.seeTheFile"
                    :arrow="false"
                    placement="right-start"
                >
                    <RouterLink v-if="companyLink" :to="companyLink">
                        <CompassObject
                            :size="CompassEnums.AvatarSize.EXTRA_SMALL"
                            :label="company.name"
                            isLink
                        />
                    </RouterLink>
                </CompassTooltip>
            </div>
            <CompassDescriptionList :variant="CompassEnums.DescriptionListVariantType.FROZEN_FIELD">
                <CompassDescriptionListItem v-if="company.email" :label="translations.email">
                    <EmailLinkCompass
                        :email="company.email"
                        :linkedid="linkedid"
                        :linkedtype="linkedtype"
                    >
                        {{ company.email }}
                    </EmailLinkCompass>
                </CompassDescriptionListItem>
                <CompassDescriptionListItem v-if="company.phone" :label="translations.phone">
                    <PhoneLinkCompass :phoneNumber="company.phone" />
                </CompassDescriptionListItem>
                <CompassDescriptionListItem v-if="hasAddress" :label="translations.address">
                    <CompassSkeleton
                        v-if="isLoading"
                        :variant="CompassEnums.SkeletonVariantType.LINE"
                        :lines="2"
                    />
                    <div
                        v-if="!isLoading"
                        class="typographies-paragraph-medium-regular overview-company-block__address"
                    >
                        <template v-for="(line, index) in addressLines" :key="index">
                            {{ line }}<br v-if="index < addressLines.length - 1" />
                        </template>
                    </div>
                </CompassDescriptionListItem>
            </CompassDescriptionList>
        </div>
    </DrawerAccordion>
</template>
<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { type RouteLocationRaw } from 'vue-router';
import CompaniesService from 'api/companies';
import { getIndividualAddresses } from 'api/individuals';
import EmailLinkCompass from 'vcomponents/overview/tabs/data/EmailLinkCompass.vue';
import PhoneLinkCompass from 'vcomponents/overview/tabs/data/PhoneLinkCompass.vue';
import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import type { Translator, PushToast } from 'js/shims-vue';

type LinkedType = 'third' | 'opportunity' | 'people';

interface Address {
    address_line_1?: string;
    address_line_2?: string;
    address_line_3?: string;
    address_line_4?: string;
    postal_code?: string;
    city?: string;
    is_invoicing_address?: boolean;
}

interface Company {
    id?: number;
    name?: string;
    email?: string;
    phone?: string;
    link?: RouteLocationRaw;
    type?: string;
    relationType?: string;
}

interface InfoItem {
    key: string;
    value: string | number | boolean | null;
}

interface Acl {
    update_individuals?: boolean;
    update_suppliers?: boolean;
    update_prospects?: boolean;
    update_clients?: boolean;
}

interface Permissions {
    acl: Acl;
}

interface AddressesResponse {
    data: { data: Address[] };
}

interface ThirdLegacy {
    edit(id?: number): void;
}

interface Props {
    linkedtype: LinkedType;
    linkedid: number;
}

const props = defineProps<Props>();

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();

const translations = {
    title: $t.__('overview.companyBlock.title'),
    edit: $t.__('common.actions.edit'),
    seeTheFile: $t.__('common.actions.see_the_file'),
    email: $t.__('common.labels.email'),
    phone: $t.__('common.labels.phone'),
    address: $t.__('common.labels.address'),
    fetchError: $t.__('common.labels.toast.error'),
};

const address = ref<Address>({});
const isLoading = ref<boolean>(true);

const permissions = computed<Permissions>(() => store.getters['user/getPermissions']);

const company = computed<Company>(() => {
    if (props.linkedtype === 'third') {
        return cleanCompanyDetails(store.state.third.dataFetch.informations);
    }
    if (props.linkedtype === 'people') {
        return store.state.people.dataFetch.thirds?.[0] ?? {};
    }
    return cleanCompanyDetails(store.state.opportunity.dataFetch.third);
});

const companyLink = computed<RouteLocationRaw | undefined>(() => {
    if (props.linkedtype === 'third') {
        return store.state.third.identity.link;
    }
    return company.value.link;
});

const hasCompany = computed<boolean>(() => Object.keys(company.value).length > 0);
const hasAddress = computed<boolean>(() => Object.keys(address.value).length > 0);

const addressLines = computed<string[]>(() => {
    if (!hasAddress.value) {
        return [];
    }
    const lastLine = [address.value.postal_code, address.value.city].filter(Boolean).join(' ');
    return [
        address.value.address_line_1,
        address.value.address_line_2,
        address.value.address_line_3,
        address.value.address_line_4,
        lastLine,
    ].filter((line): line is string => Boolean(line));
});

const id = computed<number | undefined>(() => {
    if (props.linkedtype === 'third') {
        return props.linkedid;
    }
    return company.value.id;
});

const isIndividual = computed<boolean>(
    () =>
        (props.linkedtype === 'third' && store.state.third.identity.isCorporation === false) ||
        (props.linkedtype === 'opportunity' && company.value.type === 'person') ||
        (props.linkedtype === 'people' && company.value.relationType === 'individuals'),
);

const isSupplier = computed<boolean>(
    () =>
        (props.linkedtype === 'third' && store.state.third.identity.type === 'supplier') ||
        (props.linkedtype === 'people' && company.value.type === 'supplier'),
);

const isProspect = computed<boolean>(
    () =>
        (props.linkedtype === 'third' && store.state.third.identity.type === 'prospect') ||
        (props.linkedtype === 'people' && company.value.type === 'prospect'),
);

const showEditButton = computed<boolean>(() => {
    const acl = permissions.value.acl;
    if (isIndividual.value) {
        return Boolean(acl.update_individuals);
    }
    if (isSupplier.value) {
        return Boolean(acl.update_suppliers);
    }
    if (isProspect.value) {
        return Boolean(acl.update_prospects);
    }
    return Boolean(acl.update_clients);
});

const cleanCompanyDetails = (rawCompany: InfoItem[] | null | undefined): Company => {
    if (!rawCompany) {
        return {};
    }
    return Object.fromEntries(rawCompany.map((item) => [item.key, item.value]));
};

const handleEdit = (): void => {
    (Third as ThirdLegacy).edit(id.value);
};

const loadAddresses = async (): Promise<void> => {
    if (!hasCompany.value) {
        isLoading.value = false;
        return;
    }
    const fetchAddresses = isIndividual.value
        ? getIndividualAddresses(id.value)
        : CompaniesService.getCompanyAddresses(id.value);
    try {
        const response = (await fetchAddresses) as AddressesResponse;
        if (response.data.data.length) {
            address.value = response.data.data.find((item) => item.is_invoicing_address) ?? {};
        }
    } catch {
        $pushToast({ type: 'error', icon: true, text: translations.fetchError });
    } finally {
        isLoading.value = false;
    }
};

onMounted(loadAddresses);
</script>
<style lang="scss" scoped>
.overview-company-block {
    &__company {
        margin: 0 0 var(--spacing-2);
    }
    &__address {
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
.overview-company-block :deep(.shareUrlBtn) {
    font-weight: var(--font-weights-lato-1);
}
.overview-company-block :deep(.compass-icon-button) {
    flex-shrink: 0;
}
.overview-company-block :deep(.compass-description-list__item) {
    min-width: 0; // stops overflow
}
.overview-company-block :deep(.compass-description-list__item__content) {
    min-height: var(--line-heights-20);
}
.overview-company-block :deep(.copy-element) {
    display: block;
    max-width: 100%;
}
.overview-company-block :deep(.compass-link) {
    max-width: calc(100% - 24px);
}
.overview-company-block :deep(.compass-link__label) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}
</style>
